import { evalCondition } from "@/lib/mapper/conditions";
import { evaluateExpression } from "@/lib/mapper/expression";
import { substituteExpression } from "@/modules/_shared/templateValue";
import type { PriceBranch, PriceLayer, Templatable } from "@/lib/mapper/types";
import type { CellModule } from "@/modules/_shared/contract";
import type { ResolveCtx } from "@/modules/_shared/engine";

/** Substitute the `subtotal` computed token (numeric) and field refs (string
 *  literals), evaluate the formula, and coerce to a number (NaN/invalid -> 0). */
function evalAmount(expr: Templatable | undefined, subtotal: number, ctx: ResolveCtx): number {
  const formula = substituteExpression(expr ?? "", {
    row: ctx.row,
    output: ctx.output,
    helpers: ctx.helpers,
    computed: { subtotal: String(subtotal) },
  });
  if (!formula.trim()) return 0;
  const out = evaluateExpression(formula, {});
  const n = Number(out);
  return Number.isFinite(n) ? n : 0;
}

function hasCompleteCondition(branch: PriceBranch): boolean {
  const predicates = "all" in branch.when ? branch.when.all : branch.when.any;
  return predicates.length > 0 && predicates.every((p) => p.column.trim() !== "");
}

function layerAmount(layer: PriceLayer, subtotal: number, ctx: ResolveCtx): number {
  for (const b of layer.branches ?? []) {
    if (!hasCompleteCondition(b)) continue;
    if (evalCondition(b.when, ctx.row, ctx.output)) {
      return evalAmount(b.amount?.expr ?? "0", subtotal, ctx);
    }
  }
  return evalAmount(layer.else?.expr ?? "0", subtotal, ctx);
}

function applyRounding(n: number, mode: "none" | "nearest" | "up" | "down"): number {
  switch (mode) {
    case "nearest": return Math.round(n);
    case "up": return Math.ceil(n);
    case "down": return Math.floor(n);
    default: return n;
  }
}

export const priceEngineModule: CellModule = {
  kind: "price-engine",
  capability: "cell",
  name: "Price Engine",
  tooltip: "Build a price from stacked if/else layers",
  description:
    "Calculates a price by adding up layers of if/then rules. Each layer contributes an amount and the final price is the sum.",
  category: "Rules",
  tags: ["price", "pricing", "cost", "margin", "markup", "discount", "tier", "layers", "calc"],
  icon: { kind: "lucide", name: "Layers" },
  tone: "amber",
  defaultConfig: () => ({
    kind: "price-engine",
    layers: [
      {
        label: "Base",
        branches: [],
        else: { expr: "0" },
      },
    ],
    rounding: "none",
    decimals: 2,
  }),
  resolve: (rule, ctx) => {
    if (rule.kind !== "price-engine") return "";
    if (!rule.layers || rule.layers.length === 0) return "";
    let subtotal = 0;
    for (const layer of rule.layers) subtotal += layerAmount(layer, subtotal, ctx);
    if (!Number.isFinite(subtotal)) return "";
    const rounded = applyRounding(subtotal, rule.rounding ?? "none");
    const dp = Number.isFinite(rule.decimals as number)
      ? Math.max(0, Math.min(10, Math.trunc(rule.decimals as number)))
      : 2;
    return rounded.toFixed(dp);
  },
  templateStrings: (rule) => {
    if (rule.kind !== "price-engine") return [];
    const values: Templatable[] = [];
    for (const layer of rule.layers ?? []) {
      for (const b of layer.branches ?? []) {
        if (b.amount?.expr) values.push(b.amount.expr);
        const preds = "all" in b.when ? b.when.all : b.when.any;
        for (const p of preds) {
          const col = p.column.trim();
          // out:Field predicate columns induce a workbook dependency.
          if (col.toLowerCase().startsWith("out:")) values.push(`{{${col}}}`);
        }
      }
      if (layer.else?.expr) values.push(layer.else.expr);
    }
    return values;
  },
};
